import { z } from "zod";

/**
 * Email validation schema
 * Validates proper email format
 */
export const emailSchema = z
	.string()
	.min(1, "Email is required")
	.email("Please enter a valid email address");

/**
 * Password validation schema
 * - Minimum 4 characters
 */
export const passwordSchema = z.string().min(4, "Password must be at least 4 characters");

/**
 * Simple password schema (only length requirement)
 * Used for login where we don't want to enforce complexity on existing passwords
 */
export const simplePasswordSchema = z
	.string()
	.min(1, "Password is required")
	.min(4, "Password must be at least 4 characters");

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
	email: emailSchema,
	password: z.string().min(1, "Password is required"),
});

/**
 * Signup form validation schema
 */
export const signupSchema = z.object({
	email: emailSchema,
	password: passwordSchema,
	firstName: z.string().trim().min(1, "First name is required"),
});

/**
 * Forgot password form validation schema
 */
export const forgotPasswordSchema = z.object({
	email: emailSchema,
});

/**
 * Reset password form validation schema
 */
export const resetPasswordSchema = z
	.object({
		password: passwordSchema,
		confirmPassword: z.string().min(1, "Please confirm your password"),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

/**
 * Verify email form validation schema
 */
export const verifyEmailSchema = z.object({
	email: emailSchema,
});

/**
 * Type exports for form data
 */
export type LoginFormData = z.infer<typeof loginSchema>;
export type SignupFormData = z.infer<typeof signupSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;
